import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Archive,
	CheckCircle,
	Copy01,
	Expand03,
	Plus,
	Trash02,
} from "@untitledui/icons";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableListEmptyState } from "@/components/Molecules/Table/TableListEmptyState";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { Toast } from "@/components/Molecules/Toast/Toast";
import { DeleteWorkflowRuleModal } from "@/components/Organisms/DeleteWorkflowRuleModal/DeleteWorkflowRuleModal";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import { authQueryOptions } from "@/services/auth/auth.options";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import {
	useActivateWorkflowRuleVersion,
	useArchiveWorkflowRule,
	useCreateDraftVersion,
	workflowKeys,
	workflowQueryOptions,
} from "@/services/workflow";
import type {
	WorkflowEntityTypeValue,
	WorkflowRuleDisplay,
	WorkflowRulesParams,
} from "@/services/workflow/workflow.types";
import { formatTriggerLabel } from "@/utils/workflow-rules/format";

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_COLUMN_ORDER = [
	"name",
	"trigger",
	"conditions",
	"actionsCount",
	"scope",
	"status",
	"version",
	"id",
	"rowActions",
];
const DEFAULT_SORTING = [{ id: "status", desc: false }];
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_ID = "rowActions";

const normalizeColumnOrder = (columnOrder?: string[]) => {
	const nonActionDefaultColumns = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => columnId !== ACTIONS_COLUMN_ID,
	);

	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...nonActionDefaultColumns, ACTIONS_COLUMN_ID];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter(
		(columnId) =>
			allowedColumns.has(columnId) && columnId !== ACTIONS_COLUMN_ID,
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = nonActionDefaultColumns.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults, ACTIONS_COLUMN_ID];
};

const serverSortFieldMapping: Record<string, string[]> = {
	name: ["Name"],
	status: ["Status"],
	version: ["Version"],
	id: ["Id"],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

type StatusFilter = "all" | "draft" | "active" | "archived";

// ─── Status filter ────────────────────────────────────────────────────────────

function StatusFilterSelect({
	value,
	onChange,
}: {
	value: StatusFilter;
	onChange: (v: StatusFilter) => void;
}) {
	const { t } = useAppTranslation();
	return (
		<Select
			label=""
			aria-label={t("admin.workflowRules.table.filters.status.label")}
			selectedKey={value}
			onSelectionChange={(key) => onChange(key as StatusFilter)}
			size="sm"
			className="w-36 shrink-0"
			placeholder={t("admin.workflowRules.table.filters.status.label")}
		>
			<SelectItem
				id="all"
				label={t("admin.workflowRules.table.filters.status.all")}
			/>
			<SelectItem
				id="draft"
				label={t("admin.workflowRules.table.filters.status.draft")}
			/>
			<SelectItem
				id="active"
				label={t("admin.workflowRules.table.filters.status.active")}
			/>
			<SelectItem
				id="archived"
				label={t("admin.workflowRules.table.filters.status.archived")}
			/>
		</Select>
	);
}

// ─── Per-row delete action ────────────────────────────────────────────────────

function WorkflowRuleActivateAction({
	rule,
	onActivate,
}: {
	rule: WorkflowRuleDisplay;
	onActivate: () => void;
}) {
	const { t } = useAppTranslation();
	const canActivateQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ActivateWorkflowRuleVersion",
			entityType: "WorkflowRuleVersion",
			entityId: rule.latestVersion?.id ?? "",
		}),
	);

	if (
		rule.status !== "draft" ||
		!rule.latestVersion?.id ||
		!canActivateQuery.data
	)
		return null;

	return (
		<Dropdown.Item
			id="activate"
			label={t("admin.workflowRules.table.actions.activate")}
			icon={CheckCircle}
			onAction={onActivate}
		/>
	);
}

function WorkflowRuleDeleteAction({
	rule,
	onDelete,
}: {
	rule: WorkflowRuleDisplay;
	onDelete: () => void;
}) {
	const { t } = useAppTranslation();
	const canDeleteQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: rule.id,
		}),
	);

	if (rule.status !== "draft" || !canDeleteQuery.data) return null;

	return (
		<>
			<Dropdown.Separator />
			<Dropdown.Item
				id="delete"
				label={t("admin.workflowRules.table.actions.delete")}
				icon={Trash02}
				className="[&_span]:text-red-600"
				onAction={onDelete}
			/>
		</>
	);
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface WorkflowRulesTableProps {
	/**
	 * Entity class — "job" | "ticket" | "record" | "asset"
	 * Used as the `EntityType` filter on the list API.
	 */
	entityType: WorkflowEntityTypeValue;
	/** The specific type ID (jobTypeId, ticketTypeId, etc.) sent as `EntityTypeId` to the list API. */
	entityTypeId: string;
	/** Organization-id header value (EMPTY_ORG_ID for platform admin) */
	organizationId: string;
	/**
	 * Unique persistence key for this table instance.
	 * e.g. "admin.configuration.jobTypes.{jobTypeId}.workflowRules"
	 */
	tableKey: string;
	/** Called when the user clicks the empty-state "Create Rule" button */
	onCreateRuleClick: () => void;
	/** Called when the user clicks a rule row or "View Full Details" action */
	onRuleClick?: (rule: WorkflowRuleDisplay) => void;
	/**
	 * Whether to show the empty-state Create button.
	 * Pass false for system (read-only) entity types at org admin.
	 */
	canCreateRule?: boolean;
	/**
	 * Lifecycle ID for this entity type. Required to resolve status names in the
	 * Trigger column for status_entered / status_exited rules. Pass null for
	 * entity types without a lifecycle (e.g. Record Types).
	 */
	lifecycleId?: string | null;
	/**
	 * Whether the current user has EditWorkflowRule permission.
	 * Gates createDraft row action. Defaults to true.
	 */
	canEditRule?: boolean;
	/**
	 * Whether the current user has ArchiveWorkflowRule permission.
	 * Gates archive row action. Defaults to true.
	 */
	canArchiveRule?: boolean;
	/**
	 * Drawer shows Name, Trigger, and Conditions.
	 * Page also shows Actions, Scope, and Status.
	 */
	variant?: "drawer" | "page";
}

// ─── Main component ───────────────────────────────────────────────────────────

export function WorkflowRulesTable({
	entityType,
	entityTypeId,
	organizationId,
	tableKey,
	onCreateRuleClick,
	canCreateRule = true,
	canEditRule = true,
	canArchiveRule = true,
	lifecycleId,
	onRuleClick,
	variant = "page",
}: WorkflowRulesTableProps) {
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const toastId = useId();

	// ── Local state ──
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Status"] as string[],
		"OrderBy.Descending": false,
	});
	const [filterOpen, setFilterOpen] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<{
		id: string;
		name: string;
		status: string;
	} | null>(null);

	const hasAppliedPersistedSettings = useRef(false);

	// ── Table settings persistence ──
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
		saveFilters,
	} = useTableSettings<{ statusFilter?: StatusFilter }>({
		tableKey,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: { statusFilter: "all" },
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (tableSettings.searchTerm) setSearchTerm(tableSettings.searchTerm);
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
		if (tableSettings.filters?.statusFilter) {
			setStatusFilter(tableSettings.filters.statusFilter);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.pageSize,
		tableSettings.filters,
	]);

	// ── API params ──
	const debouncedSearch = useDebounced(searchTerm, 500);
	const apiStart = pageIndex * pageSize + 1;

	const queryParams = useMemo<WorkflowRulesParams>(
		() => ({
			EntityType: entityType,
			...(entityTypeId ? { EntityTypeId: entityTypeId } : {}),
			...(debouncedSearch.trim() ? { SearchTerm: debouncedSearch.trim() } : {}),
			...(statusFilter !== "all" ? { Status: statusFilter } : {}),
			...sortParams,
		}),
		[entityType, entityTypeId, debouncedSearch, statusFilter, sortParams],
	);

	const countQueryParams = useMemo<WorkflowRulesParams>(
		() => ({
			EntityType: entityType,
			...(entityTypeId ? { EntityTypeId: entityTypeId } : {}),
			...(debouncedSearch.trim() ? { SearchTerm: debouncedSearch.trim() } : {}),
			...(statusFilter !== "all" ? { Status: statusFilter } : {}),
		}),
		[entityType, entityTypeId, debouncedSearch, statusFilter],
	);

	// ── Queries ──
	const countQuery = useQuery(
		workflowQueryOptions.rules.count(countQueryParams, { organizationId }),
	);
	const pageQuery = useQuery(
		workflowQueryOptions.rules.list(apiStart, pageSize, queryParams, {
			organizationId,
		}),
	);

	const displayRows = pageQuery.data ?? [];

	const pageCountServer = useMemo(() => {
		const total = countQuery.data ?? 0;
		return total ? Math.ceil(total / pageSize) : 0;
	}, [countQuery.data, pageSize]);

	// ── Lifecycle status name lookup ──
	// Fetches GET /workflows/status-lifecycles/{id} which includes the full
	// statuses list. Only runs when a lifecycleId is available (job/ticket types).
	const lifecycleDetailQuery = useQuery({
		...lifecycleQueryOptions.statusLifecycleDetail(
			lifecycleId ?? "",
			organizationId,
		),
		enabled: !!lifecycleId,
	});
	const statusNameMap = useMemo<Record<string, string>>(() => {
		const statuses = lifecycleDetailQuery.data?.statuses ?? [];
		const map: Record<string, string> = {};
		for (const s of statuses) {
			if (s.id && s.name) map[s.id.toLowerCase()] = s.name;
		}
		return map;
	}, [lifecycleDetailQuery.data]);

	// ── Mutations ──
	const archiveMutation = useArchiveWorkflowRule();
	const activateMutation = useActivateWorkflowRuleVersion();
	const draftMutation = useCreateDraftVersion();

	// ── Pagination guard ──
	useEffect(() => {
		const total = countQuery.data ?? 0;
		const pages = Math.max(1, Math.ceil(total / pageSize));
		if (pageIndex > 0 && pageIndex >= pages) {
			setPageIndex(Math.max(0, pages - 1));
		}
	}, [countQuery.data, pageSize, pageIndex]);

	const onSortChange = useCallback(
		({ fields, descending }: { fields: string[]; descending: boolean }) => {
			setSortParams({
				"OrderBy.Fields": fields,
				"OrderBy.Descending": descending,
			});
		},
		[],
	);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleActivate = useCallback(
		(rule: WorkflowRuleDisplay) => {
			const versionId = rule.latestVersion?.id;
			if (!versionId) return;
			activateMutation.mutate(
				{ ruleId: rule.id, versionId, organizationId },
				{
					onSuccess: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="success"
									addonText="Success"
									message={t("admin.workflowRules.table.toasts.activated")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
					onError: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="error"
									addonText="Error"
									message={t("admin.workflowRules.table.toasts.activateFailed")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
				},
			);
		},
		[activateMutation, organizationId, t, toastId],
	);

	const handleArchive = useCallback(
		(rule: WorkflowRuleDisplay) => {
			archiveMutation.mutate(
				{ ruleId: rule.id, organizationId },
				{
					onSuccess: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="success"
									addonText="Success"
									message={t("admin.workflowRules.table.toasts.archived")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
					onError: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="error"
									addonText="Error"
									message={t("admin.workflowRules.table.toasts.archiveFailed")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
				},
			);
		},
		[archiveMutation, organizationId, t, toastId],
	);

	const handleCreateDraft = useCallback(
		(rule: WorkflowRuleDisplay) => {
			draftMutation.mutate(
				{ ruleId: rule.id, organizationId },
				{
					onSuccess: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="success"
									addonText="Success"
									message={t("admin.workflowRules.table.toasts.draftCreated")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
					onError: () =>
						toast.custom(
							(toastIdParam) => (
								<Toast
									id={toastId}
									color="error"
									addonText="Error"
									message={t("admin.workflowRules.table.toasts.draftFailed")}
									showCloseButton
									onClose={() => toast.dismiss(toastIdParam)}
								/>
							),
							{ duration: 5000 },
						),
				},
			);
		},
		[draftMutation, organizationId, t, toastId],
	);

	// ── Column labels ──
	const columnLabels = useMemo(
		() => ({
			name: t("admin.workflowRules.table.columns.name"),
			trigger: t("admin.workflowRules.table.columns.trigger"),
			conditions: t("admin.workflowRules.table.columns.conditions"),
			actionsCount: t("admin.workflowRules.table.columns.actions"),
			scope: t("admin.workflowRules.table.columns.scope"),
			status: t("admin.workflowRules.table.columns.status"),
			version: t("admin.workflowRules.table.columns.version"),
			id: t("admin.workflowRules.table.columns.id"),
		}),
		[t],
	);

	const handleViewFullDetails = useCallback(
		(rule: WorkflowRuleDisplay) => {
			if (onRuleClick) {
				onRuleClick(rule);
			} else {
				console.log("rule row clicked — view full details", rule);
			}
		},
		[onRuleClick],
	);

	// ── Columns ──
	const columns = useMemo<ColumnDef<WorkflowRuleDisplay>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				header: columnLabels.name,
				enableSorting: true,
				size: 240,
				meta: {
					headerProps: { isRowHeader: true },
				},
				cell: ({ row }) => {
					const rule = row.original;
					return (
						<span className="font-medium text-gray-900">
							{rule.name ?? <EmptyValue />}
						</span>
					);
				},
			},
			{
				id: "trigger",
				header: columnLabels.trigger,
				enableSorting: false,
				size: 160,
				cell: ({ row }) => {
					const trigger = row.original.latestVersion?.trigger;
					if (!trigger) return <EmptyValue />;
					const label = formatTriggerLabel(trigger.type, t);
					const config = trigger.config as
						| Record<string, unknown>
						| null
						| undefined;
					// Backend may return StatusId (PascalCase) or statusId (camelCase)
					const rawStatusId =
						(config?.StatusId as string | undefined) ??
						(config?.statusId as string | undefined);
					const statusDetail = rawStatusId
						? (statusNameMap[rawStatusId.toLowerCase()] ?? rawStatusId)
						: null;
					return (
						<div className="flex flex-col gap-0.5">
							<span className="font-medium text-gray-900">{label}</span>
							{statusDetail ? (
								<span className="truncate text-sm text-gray-500">
									{statusDetail}
								</span>
							) : null}
						</div>
					);
				},
			},
			{
				id: "conditions",
				header: columnLabels.conditions,
				enableSorting: false,
				size: 100,
				cell: ({ row }) => {
					const count =
						row.original.latestVersion?.condition?.conditions?.length ?? 0;
					if (count === 0) {
						return (
							<span className="text-sm text-tertiary">
								{t("admin.workflowRules.table.conditions.always")}
							</span>
						);
					}
					return <span className="text-sm text-secondary">{count}</span>;
				},
			},
			{
				id: "actionsCount",
				header: columnLabels.actionsCount,
				enableSorting: false,
				size: 80,
				cell: ({ row }) => {
					const count = row.original.latestVersion?.actions?.length ?? 0;
					return <span className="text-sm text-secondary">{count}</span>;
				},
			},
			{
				id: "scope",
				header: columnLabels.scope,
				enableSorting: false,
				size: 120,
				cell: () => (
					<span className="text-sm text-gray-700">
						{t("admin.workflowRules.table.scope.organization")}
					</span>
				),
			},
			{
				id: "status",
				accessorKey: "status",
				header: columnLabels.status,
				enableSorting: true,
				size: 100,
				cell: ({ row }) => {
					const s = row.original.status;
					if (s === "active") {
						return (
							<BadgeWithDot type="pill-color" color="success" size="sm">
								{t("admin.workflowRules.table.filters.status.active")}
							</BadgeWithDot>
						);
					}
					if (s === "archived") {
						return (
							<BadgeWithDot type="pill-color" color="gray" size="sm">
								{t("admin.workflowRules.table.filters.status.archived")}
							</BadgeWithDot>
						);
					}
					return (
						<BadgeWithDot type="pill-color" color="gray" size="sm">
							{t("admin.workflowRules.table.filters.status.draft")}
						</BadgeWithDot>
					);
				},
			},
			{
				id: "version",
				header: columnLabels.version,
				enableSorting: false,
				size: 80,
				cell: ({ row }) => {
					const v = row.original.latestVersion?.version;
					return v != null ? (
						<span className="text-sm text-gray-700">{v}</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "id",
				accessorKey: "id",
				header: columnLabels.id,
				enableSorting: false,
				size: 160,
				cell: ({ row }) => <CopyableId value={row.original.id} />,
			},
			{
				id: "rowActions",
				header: getActionsColumnHeader(t),
				enableSorting: false,
				enableHiding: false,
				size: 56,
				meta: {
					disableColumnReorder: true,
					rowFullViewAction: (row: unknown) =>
						handleViewFullDetails(row as WorkflowRuleDisplay),
					cellProps: {
						className: "w-14 px-2",
						"data-suppress-row-click": true,
					},
				},
				cell: ({ row }) => {
					const rule = row.original;
					const isActive = rule.status === "active";
					return (
						<div className="flex justify-end" data-suppress-row-click>
							<Dropdown.Root>
								<Dropdown.DotsButton
									aria-label={t(
										"admin.workflowRules.table.actionsMenu.ariaLabel",
									)}
								/>
								<Dropdown.Popover className="w-min">
									<Dropdown.Menu>
										<Dropdown.Item
											id="view"
											label={t(
												"admin.workflowRules.table.actions.viewFullDetails",
											)}
											icon={Expand03}
											onAction={() => handleViewFullDetails(rule)}
										/>
										<WorkflowRuleActivateAction
											rule={rule}
											onActivate={() => handleActivate(rule)}
										/>
										{isActive && canArchiveRule && (
											<Dropdown.Item
												id="archive"
												label={t("admin.workflowRules.table.actions.archive")}
												icon={Archive}
												onAction={() => handleArchive(rule)}
											/>
										)}
										{isActive && canEditRule && (
											<Dropdown.Item
												id="createDraft"
												label={t(
													"admin.workflowRules.table.actions.createDraftVersion",
												)}
												icon={Copy01}
												onAction={() => handleCreateDraft(rule)}
											/>
										)}
										<WorkflowRuleDeleteAction
											rule={rule}
											onDelete={() =>
												setPendingDelete({
													id: rule.id,
													name: rule.name ?? rule.id,
													status: rule.status,
												})
											}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					);
				},
			},
		],
		[
			canEditRule,
			canArchiveRule,
			columnLabels,
			handleActivate,
			handleArchive,
			handleCreateDraft,
			handleViewFullDetails,
			statusNameMap,
			t,
		],
	);

	// ── Active filter check ──
	const hasActiveFilters =
		debouncedSearch.trim().length > 0 || statusFilter !== "all";

	const isEmpty =
		!pageQuery.isLoading &&
		!pageQuery.isFetching &&
		!countQuery.isLoading &&
		!countQuery.isFetching &&
		(countQuery.data ?? 0) === 0;

	const hasSearch = debouncedSearch.trim().length > 0 || statusFilter !== "all";

	// ── Error state ──
	const loadError = pageQuery.error ?? countQuery.error;
	if (loadError) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<h3 className="text-sm font-medium text-error">
					{t("admin.workflowRules.table.errors.failedToLoad")}
				</h3>
				<p className="mt-1 text-sm text-tertiary">{loadError.message}</p>
				<Button
					size="sm"
					color="primary"
					onClick={handleRefresh}
					className="mt-3"
				>
					{t("common.actions.tryAgain")}
				</Button>
			</div>
		);
	}

	if (isEmpty && !hasSearch) {
		return (
			<TableListEmptyState
				title={t("admin.workflowRules.emptyState.title")}
				description={t("admin.workflowRules.emptyState.description")}
				actionLabel={
					canCreateRule ? t("common.actions.create") : undefined
				}
				onAction={canCreateRule ? onCreateRuleClick : undefined}
			/>
		);
	}

	return (
		<>
			<CustomTableWrapper<WorkflowRuleDisplay>
				tableKey={tableKey}
				data={displayRows}
				columns={columns}
				showHeaderPadding={false}
				zebraStriping
				isLoading={pageQuery.isLoading}
				isFetching={pageQuery.isFetching || countQuery.isFetching}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				paginationMode="server"
				externalPaginationState={{ pageSize, pageIndex }}
				onPaginationChange={({ pageSize: ps, pageIndex: pi }) => {
					if (ps !== pageSize) {
						setPageSize(ps);
						setPageIndex(0);
						savePageSize(ps);
					} else if (pi !== pageIndex) {
						setPageIndex(pi);
					}
				}}
				pageCount={pageCountServer}
				rowCount={countQuery.data}
				totalRowCount={countQuery.data}
				sortFieldMapping={serverSortFieldMapping}
				defaultSortField="status"
				onSortChange={onSortChange}
				filterPlaceholder={t("admin.workflowRules.table.search.placeholder")}
				filterValue={searchTerm}
				onDebouncedFilterChange={(s) => {
					setSearchTerm(s);
					setPageIndex(0);
					saveSearchTerm(s);
				}}
				filterLayout="bar"
				filtersTrailingContent={
					<StatusFilterSelect
						value={statusFilter}
						onChange={(v) => {
							setStatusFilter(v);
							setPageIndex(0);
							saveFilters({ statusFilter: v });
						}}
					/>
				}
				filterBarTrailing={
					canCreateRule ? (
						<Button
							color="accent"
							size="sm"
							iconLeading={Plus}
							onClick={onCreateRuleClick}
						>
							{t("common.actions.create")}
						</Button>
					) : null
				}
				emptyState={
					hasSearch ? (
						<TableSearchEmptyState
							titleKey="admin.workflowRules.table.empty.search.title"
							descriptionKey="admin.workflowRules.table.empty.search.description"
						/>
					) : undefined
				}
				enableTableActions
				isFilterOpen={filterOpen}
				hasActiveFilters={hasActiveFilters}
				onFilterClick={() => setFilterOpen((o) => !o)}
				enableColumnReordering
				enableColumnResizing
				onRowClick={(row) => {
					if (onRuleClick) {
						onRuleClick(row);
					} else {
						console.log("rule row clicked", row);
					}
				}}
				getRowId={(row) => row.id}
				initialSortingState={tableSettings.sorting}
				initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
				initialColumnVisibility={{
					version: false,
					id: false,
					...(variant === "drawer"
						? { actionsCount: false, scope: false, status: false }
						: {}),
					...tableSettings.columnVisibility,
					rowActions: true,
				}}
				settingsLoaded={settingsLoaded}
				onSortingStateChange={saveSorting}
				onColumnOrderChange={(order) => {
					saveColumnOrder(normalizeColumnOrder(order));
				}}
				onColumnVisibilityChange={saveColumnVisibility}
				initialSorting={DEFAULT_SORTING}
			/>

			<DeleteWorkflowRuleModal
				isOpen={!!pendingDelete}
				setIsOpen={(open) => {
					if (!open) setPendingDelete(null);
				}}
				handleClose={() => setPendingDelete(null)}
				onDeleteSuccess={() => setPendingDelete(null)}
				ruleId={pendingDelete?.id ?? ""}
				ruleName={pendingDelete?.name ?? ""}
				ruleStatus={pendingDelete?.status ?? ""}
				organizationId={organizationId}
			/>
		</>
	);
}
